import { Injectable, Injector, inject } from '@angular/core';
import { Params, Router } from '@angular/router';
import {
  CompanyTerminationDetails,
  TerminatedAudience,
} from '../../models/smartclock/company-access.model';
import { AuthService } from '../auth/auth.service';
import { AuthStorageService } from '../auth/auth-storage.service';
import { SessionService } from '../smartclock/session.service';
import { SupportSessionService } from '../support/support-session.service';
import { AccessService } from './access.service';

/** Página pública «El servicio de SmartClock terminó» (grupo sin guards, AuthLayout). */
export const SERVICE_ENDED_URL = '/servicio-terminado';

/** Query params de la página; los arma este servicio y los lee la página. */
export const SERVICE_ENDED_PARAMS = {
  company: 'company',
  lastDay: 'lastDay',
  until: 'until',
  profile: 'profile',
} as const;

/**
 * Término de la suscripción de la empresa del lado de la sesión
 * (subscription-termination.spec.md del API, «Aplicación del acceso»).
 *
 * `403 COMPANY_TERMINATED` (en cualquier request con sesión, incluido `POST /auth/context`) o un
 * `/access/me` que deja a la persona fuera: lleva a la página pública con el nombre de la empresa y
 * las fechas, y cierra la sesión. En modo soporte («Ver como») sólo sale del modo soporte: nunca
 * toca la sesión normal que comparten las otras pestañas.
 */
@Injectable({ providedIn: 'root' })
export class CompanyTerminationService {
  private readonly router = inject(Router);
  private readonly authStorage = inject(AuthStorageService);
  private readonly access = inject(AccessService);
  private readonly session = inject(SessionService);
  // Diferido: AuthService y SupportSessionService dependen de HttpClient, que construye la cadena
  // de interceptores que usa este servicio.
  private readonly injector = inject(Injector);

  /** Hay un cierre en curso: una pantalla dispara varias llamadas que fallan juntas. */
  private ending = false;

  /**
   * La sesión en curso quedó sin servicio. Navega PRIMERO y cierra la sesión DESPUÉS (como el fin
   * de una sesión de soporte): mientras la vista siga montada, sus llamadas viajan con el token de
   * esta sesión y nunca con otra. Si la navegación no se completa, la sesión se cierra igual.
   */
  end(details: CompanyTerminationDetails): void {
    if (this.ending) return;
    this.ending = true;
    // Se decide ANTES de navegar: el modo soporte puede cambiar mientras tanto.
    const supportMode = this.authStorage.isSupportMode();
    const done = () => {
      this.ending = false;
      if (supportMode) {
        this.injector.get(SupportSessionService).leave();
      } else {
        this.injector.get(AuthService).logout();
      }
    };
    this.router
      .navigate([SERVICE_ENDED_URL], {
        queryParams: serviceEndedQueryParams(details, this.audienceOfSession()),
      })
      .then(done, done);
  }

  /** Igual que `end()`, con la empresa y las fechas que informó `/access/me`. */
  endFromAccess(): void {
    const company = this.access.company();
    const access = this.access.companyAccess();
    this.end({
      companyId: company?.id ?? null,
      companyName: company?.name ?? null,
      lastServiceDay: access?.lastServiceDay ?? null,
      readOnlyUntil: access?.readOnlyUntil ?? null,
    });
  }

  /**
   * Login rechazado con `403 COMPANY_TERMINATED`: no hay sesión que cerrar. El API informa el
   * perfil en `details.profile`; si no viniera, una consulta todavía vigente sólo puede ser de un
   * trabajador (los administrativos entran durante la consulta) y si no, el texto general.
   */
  showForLogin(details: CompanyTerminationDetails, now: Date = new Date()): void {
    const until = details.readOnlyUntil ? new Date(details.readOnlyUntil).getTime() : NaN;
    const audience: TerminatedAudience | null =
      details.profile ?? (until > now.getTime() ? 'worker' : null);
    void this.router.navigate([SERVICE_ENDED_URL], {
      queryParams: serviceEndedQueryParams(details, audience),
    });
  }

  private audienceOfSession(): TerminatedAudience {
    const role = this.access.role() ?? this.session.role;
    return role === 'worker' ? 'worker' : 'admin';
  }
}

/** Query params de la página: sólo los valores presentes. */
export function serviceEndedQueryParams(
  details: CompanyTerminationDetails,
  audience: TerminatedAudience | null,
): Params {
  const params: Params = {};
  if (details.companyName) params[SERVICE_ENDED_PARAMS.company] = details.companyName;
  if (details.lastServiceDay) params[SERVICE_ENDED_PARAMS.lastDay] = details.lastServiceDay;
  if (details.readOnlyUntil) params[SERVICE_ENDED_PARAMS.until] = details.readOnlyUntil;
  if (audience) params[SERVICE_ENDED_PARAMS.profile] = audience;
  return params;
}
